import { OrderStatusEnum } from '#/constants/order-status';
import { BeaconStatus } from '#/constants/pectra/beacon-status';
import { CredentialType } from '#/constants/pectra/credential-type';
import { ValidatorEventEnum } from '#/constants/pectra/validator-event';
import { ValidatorStatusEnum } from '#/constants/validator-status';
import { PaginationObject, ResponseObject } from '#/interfaces';
import request from '#/utils/request';
import { QueryFunctionContext } from '@tanstack/react-query';
import { Address, Hex, keccak256, toHex } from 'viem';

export interface EthereumStakingOrderInfo {
  count: number;
  amount: number;
  activeNum: number;
  exitNum: number;
  slashedNum: number;
  withdrawaddr: Address;
  protocolRewards: string;
  funding: string;
  claimaddr: Address;
  status: OrderStatusEnum; // "waiting","payFailed","pending","active","exit","slashed","exiting"
  hash: string;
  pubkeys: Hex[];
  signatures: Hex[];
  paramsSig: Hex;
  extradata: number;
  createTime: number;
  stake: number;
  tips: number;
  validators?: EthereumStakingValidatorInfo[];
}

export interface ValidatorEvent {
  pubKey: Hex;
  event: ValidatorEventEnum;
  createTime: number;
}

export interface EthereumStakingValidatorInfo {
  withdrawaddr: Address;
  claimaddr: Address;
  validatorIndex: string | null;
  validatorId: number;
  pubKey: Hex;
  balance: string;
  protocolRewards: string;
  activation: number;
  status: ValidatorStatusEnum;
  exit: number;
  effectiveBalance: string;
  credentialType: CredentialType;
  beaconStatus: BeaconStatus | string;
  activeEpoch: number;
  lastUpdate: number;
  event: ValidatorEvent[] | null;
}

function fetchOrderList(data: OrderListQueryParams) {
  return request.post<
    ResponseObject<PaginationObject<EthereumStakingOrderInfo>>
  >('/api/v1/eth2/order/list', data);
}

export const orderList_queryId = keccak256(toHex('orderList'));

interface OrderListQueryParams {
  searchKey?: string;
  keywords?: string;
  status?: string[]; // [] 代表所有
  limit?: number;
  offset?: number;
  startTime?: number;
  endTime?: number;
  sortBy?: string | null;
  sortDirection?: string | null; // 'asc' | 'desc'
}

export const orderList_queryKey = ({
  searchKey,
  keywords,
  ...remainingParams
}: OrderListQueryParams) =>
  [
    {
      searchKey: keywords ? searchKey : undefined,
      keywords: keywords ? `%${keywords}%` : undefined,
      ...remainingParams,
    },
    orderList_queryId,
  ] as const;

export const orderList_queryFn = async (
  context: QueryFunctionContext<ReturnType<typeof orderList_queryKey>>
) => {
  const [{ limit = 10, offset = 0, ...remainingParams }] = context.queryKey;
  return fetchOrderList({
    ...remainingParams,
    offset,
    limit,
  });
};

export const orderList_infiniteQueryKey = orderList_queryKey;

export const orderList_infiniteQueryFn = async (
  context: QueryFunctionContext<ReturnType<typeof orderList_queryKey>>
) => {
  const [{ limit = 10, offset: offset_, ...remainingParams }] =
    context.queryKey;
  const pageParam = context.pageParam as number;

  const data = await fetchOrderList({
    ...remainingParams,
    limit,
    offset: pageParam * limit,
  });

  const total = data.data.data?.total ?? 0;
  const offset = data.data.data?.offset || 0;
  const nextPageParam = offset + limit < total ? offset / limit + 1 : null;
  return { data: data.data.data?.data, nextPageParam };
};
